/**
 * The mandatory time-window control (ADR-038 Phase F; D5 / D18).
 *
 * Every query carries a time window — it is the single largest
 * performance lever (chunk exclusion on every query, and a bound on both
 * the scan and the count), and the BFF rejects a query without one rather
 * than silently converting it to a full scan. So this control has no
 * "any time" option: there is no such query.
 *
 * Relative is the default and the first-class mode (D18 / REQ-UI-126) —
 * a window pinned to absolute instants is stale the next day, and a saved
 * query is meant to re-run against a live window. Absolute is available
 * for the forensic case where the instants *are* the question.
 *
 * Cross-references:
 *   - REQ-QUERY-032 (a query without a window is rejected); D5.
 *   - REQ-UI-126 (a saved query records a relative window by default).
 */
import { RELATIVE_WINDOWS, type TimeRangeSelection } from "@/query/query-model";
import { cn } from "@/lib/utils";

export interface QueryTimeRangeControlProps {
  readonly value: TimeRangeSelection;
  readonly onChange: (next: TimeRangeSelection) => void;
  /** Validation message for the current selection, when it is unusable. */
  readonly error?: string | null;
  readonly testId?: string;
}

/** Render the time-window control. */
export function QueryTimeRangeControl({
  value,
  onChange,
  error = null,
  testId = "query-time-range",
}: QueryTimeRangeControlProps): React.ReactElement {
  const inputClass =
    "rounded border border-border bg-bg-base px-1.5 py-1 text-body-sm text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-accent";

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      <span className="text-meta uppercase tracking-wide text-text-secondary">Window</span>

      <select
        aria-label="Time window mode"
        value={value.mode}
        onChange={(event) =>
          onChange(
            event.target.value === "relative"
              ? { mode: "relative", durationSeconds: 86_400 }
              : { mode: "absolute", start: "", end: "" },
          )
        }
        data-testid={`${testId}-mode`}
        className={inputClass}
      >
        <option value="relative">Relative</option>
        <option value="absolute">Absolute</option>
      </select>

      {value.mode === "relative" ? (
        <select
          aria-label="Relative time window"
          value={String(value.durationSeconds)}
          onChange={(event) =>
            onChange({ mode: "relative", durationSeconds: Number(event.target.value) })
          }
          data-testid={`${testId}-relative`}
          className={inputClass}
        >
          {RELATIVE_WINDOWS.map((window) => (
            <option key={window.id} value={String(window.seconds)}>
              {window.label}
            </option>
          ))}
        </select>
      ) : (
        <>
          <input
            type="datetime-local"
            aria-label="Window start"
            value={value.start}
            onChange={(event) => onChange({ ...value, start: event.target.value })}
            data-testid={`${testId}-start`}
            className={inputClass}
          />
          <span className="text-text-muted">to</span>
          <input
            type="datetime-local"
            aria-label="Window end"
            value={value.end}
            onChange={(event) => onChange({ ...value, end: event.target.value })}
            data-testid={`${testId}-end`}
            className={inputClass}
          />
        </>
      )}

      {error !== null && (
        <span
          role="alert"
          className={cn("text-body-sm text-destructive")}
          data-testid={`${testId}-error`}
        >
          {error}
        </span>
      )}
    </div>
  );
}
